/**
 * Centralized Date Formatting Utility
 * 
 * All dates displayed to users should use MM/DD/YYYY format.
 * This utility provides consistent formatting throughout the application.
 */

/**
 * Format a date string or Date object to MM/DD/YYYY format
 * @param {string|Date} dateInput - Date string (ISO, YYYY-MM-DD) or Date object
 * @returns {string} Formatted date string in MM/DD/YYYY format, or empty string if invalid
 */
export const formatDate = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    let date;
    
    // Handle YYYY-MM-DD format without timezone conversion
    if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      const [year, month, day] = dateInput.split('-').map(Number);
      date = new Date(year, month - 1, day);
    }
    // Handle ISO format with T separator - extract date part only
    else if (typeof dateInput === 'string' && dateInput.includes('T')) {
      const datePart = dateInput.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      date = new Date(year, month - 1, day);
    }
    // Handle Date object or other string formats
    else {
      date = new Date(dateInput);
    }
    
    if (isNaN(date.getTime())) return '';
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (e) {
    console.error('Date formatting error:', e);
    return '';
  }
};

/**
 * Format a date with time to MM/DD/YYYY HH:MM AM/PM format
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    
    // e.g. Nov 3, 2026, 2:30 PM
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  } catch (e) {
    console.error('DateTime formatting error:', e);
    return '';
  }
};

/**
 * Format a date with full time including seconds to MM/DD/YYYY HH:MM:SS AM/PM format
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date and time string with seconds
 */
export const formatDateTimeWithSeconds = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    
    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
  } catch (e) {
    console.error('DateTime formatting error:', e);
    return '';
  }
};

/**
 * Get today's date in YYYY-MM-DD format for input fields
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayForInput = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a date input value (YYYY-MM-DD) and return display format (MM/DD/YYYY)
 * @param {string} inputValue - Date in YYYY-MM-DD format from input field
 * @returns {string} Display formatted date
 */
export const inputToDisplay = (inputValue) => {
  return formatDate(inputValue);
};

export default {
  formatDate,
  formatDateTime,
  formatDateTimeWithSeconds,
  getTodayForInput,
  inputToDisplay
};
